import React, { useEffect, useState, useCallback } from "react";
import { getEmployeeLeaveSummary } from "../../http";
import { toast } from "react-toastify";
import Loading from "../Loading";

const LeaveManagement = () => {
    const [leaveSummaryData, setLeaveSummaryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');

    const fetchLeaveSummary = useCallback(async () => {
        try {
            setLoading(true);
            const payload = {
                year: selectedYear,
                month: selectedMonth.padStart(2, '0')
            };
            
            const response = await getEmployeeLeaveSummary(payload);
            
            // Handle different response structures
            if (Array.isArray(response)) {
                // Direct array response
                setLeaveSummaryData(response);
                toast.success(`Employee records Fetched Successfully`);
            } else if (response && response.success && Array.isArray(response.data)) {
                // Wrapped response with success property
                setLeaveSummaryData(response.data);
                toast.success(`Employee records Fetched Successfully`);
            } else if (response && Array.isArray(response.data)) {
                // Response with data property but no success flag
                setLeaveSummaryData(response.data);
                toast.success(`Employee records Fetched Successfully`);
            } else if (response && typeof response === 'object' && response.status === 200) {
                // Object with numeric indices (like {0: {...}, 1: {...}, status: 200})
                const dataArray = Object.keys(response)
                    .filter(key => !isNaN(key) && response[key] && response[key]._id)
                    .map(key => response[key]);
                setLeaveSummaryData(dataArray);
                toast.success(`Employee records Fetched Successfully`);
            } else {
                toast.error(response?.message || 'No data found for the selected month and year');
                setLeaveSummaryData([]);
            }
        } catch (error) {
            toast.error('Error fetching leave summary data: ' + (error.message || 'Unknown error'));
            setLeaveSummaryData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    // Get current month and year
    useEffect(() => {
        const currentDate = new Date();
        const currentMonth = (currentDate.getMonth() + 1).toString();
        const currentYear = currentDate.getFullYear().toString();
        
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
    }, []);

    // Fetch data when component mounts or when month/year changes
    useEffect(() => {
        if (selectedMonth && selectedYear) {
            fetchLeaveSummary();
        }
    }, [selectedMonth, selectedYear, fetchLeaveSummary]);

    const handleSearch = () => {
        if (selectedMonth && selectedYear) {
            fetchLeaveSummary();
        } else {
            toast.error('Please select both month and year');
        }
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
        <>
            <div className="main-content">
                <section className="section">
                    <div className="card">
                        <div className="card-header">
                            <h4>Leave Management</h4>
                        </div>
                        <div className="card-body">
                            <div className="d-flex flex-wrap justify-content align-items-center-center w-100 gap-2">
                                <div className="col-md col-12 mb-2">
                                    <select
                                        className="form-control select2 w-100"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                    >
                                        <option value="">Select Month</option>
                                        {monthNames.map((month, index) => (
                                            <option key={index + 1} value={index + 1}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md col-12 mb-2">
                                    <select
                                        name="year"
                                        className="form-control select2 w-100"
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(e.target.value)}
                                    >
                                        <option value="">Select Year</option>
                                        <option value="2023">2023</option>
                                        <option value="2024">2024</option>
                                        <option value="2025">2025</option>
                                        <option value="2026">2026</option>
                                        <option value="2027">2027</option>
                                        <option value="2028">2028</option>
                                    </select>
                                </div>

                                <div className="col-md col-12 mb-2">
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleSearch}
                                        disabled={loading}
                                    >
                                        {loading ? 'Loading...' : 'Search'}
                                    </button>
                                </div>
                            </div>
                            
                            {loading ? (
                                <Loading />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Employee Name</th>
                                                <th>Last Month Available Leave</th>
                                                <th>Leaves Taken</th>
                                                <th>Allowed Leaves</th>
                                                <th>Monthly Allowed Leave</th>
                                                <th>Loss of Pay</th>
                                                <th>Current Available Leave</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leaveSummaryData.length > 0 ? (
                                                leaveSummaryData.map((employee, index) => (
                                                    <tr key={employee._id || index}>
                                                        <td>{employee.name}</td>
                                                        <td>{employee.lastMonthAvailableLeave}</td>
                                                        <td>{employee.leaveTaken}</td>
                                                        <td>{employee.allowedLeaves}</td>
                                                        <td>{employee.monthlyAllowedLeave}</td>
                                                        <td>{employee.lossOfPay}</td>
                                                        <td>{employee.currentAvailableLeave}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center">
                                                        {loading ? 'Loading...' : 'No data available for the selected month and year'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </>
    )

}

export default LeaveManagement;